import React, { useState, Suspense, useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Ground, Asphalt } from './components/Ground';
import Overlay from './components/Overlay';
import LoadingScreen from './components/LoadingScreen';
import { Building } from './components/Building';
import Car from './components/Car';
import Sound from './components/Sound';
import SoundVisualization from './components/SoundVisualization';
import ExhaustSmoke from './components/ExhaustSmoke';
import EndText from './components/EndText';
import { INTRO_COLORS, CAMERA_ANIMATION, DEBUG } from './constants';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { CatmullRomCurve3, Vector3, BufferGeometry, LineBasicMaterial, Line, SphereGeometry, MeshBasicMaterial, Mesh } from 'three';
import { WebSocketProvider } from './contexts/WebSocketContext';

// Регистрируем плагин
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollToPlugin);
}

function Scene({ 
  groundType, 
  spotlightColors, 
  isMoving, 
  isCarVisible, 
  spotlightIntensity, 
  fadeSpeed, 
  isEngineOn, 
  soundGraphics,
  onEndPositionChange 
}) {
  // Add logging
  useEffect(() => {
    console.log('Scene: groundType changed to', groundType);
  }, [groundType]);

  useEffect(() => {
    console.log('Scene: spotlightColors changed to', spotlightColors);
  }, [spotlightColors]);

  const spotlightIntensitySecondary = spotlightIntensity * 0.67;
  const smokeRef = useRef();
  const { camera, scene } = useThree();
  const animationRef = useRef(null);
  const curveRef = useRef(null);
  const lookAtCurveRef = useRef(null);
  
  // Define building spawn positions
  const startSpawningPositionLeft = [14, 0, 40];
  const startSpawningPositionRight = [-5, 0, 50];

  // Move buildings state to Scene component
  const [buildings, setBuildings] = useState([
    { id: Date.now(), position: [...startSpawningPositionRight], hasSpawned: false, side: 'right' },
    { id: Date.now() + 1, position: [...startSpawningPositionLeft], hasSpawned: false, side: 'left' }
  ]);

  // Обновляем кривую при изменении точек
  useEffect(() => {
    // Создаем массив Vector3 из точек для позиции камеры
    const positionPoints = CAMERA_ANIMATION.CURVE_POINTS.map(point => new Vector3(...point));
    curveRef.current = new CatmullRomCurve3(positionPoints);

    // Создаем массив Vector3 из точек для направления камеры
    const lookAtPoints = CAMERA_ANIMATION.LOOK_AT_POINTS.map(point => new Vector3(...point));
    lookAtCurveRef.current = new CatmullRomCurve3(lookAtPoints);
  }, []);

  // Handle building spawning
  useFrame((state, delta) => {
    if (isMoving) {
      setBuildings(prevBuildings => {
        const newBuildings = [];
        
        prevBuildings.forEach(building => {
          if (building.position[2] < -5 && !building.hasSpawned) {
            const originalPosition = building.side === 'right' ? startSpawningPositionRight : startSpawningPositionLeft;
            newBuildings.push({
              id: Date.now(),
              position: [...originalPosition],
              hasSpawned: false,
              side: building.side
            });
          }
          
          if (building.position[2] > -70) {
            newBuildings.push({
              ...building,
              hasSpawned: building.position[2] < -5 ? true : building.hasSpawned
            });
          }
        });
        
        return newBuildings;
      });
    }
  });

  // Визуализация кривой и контрольных точек
  useEffect(() => {
    if (!curveRef.current || !lookAtCurveRef.current) return;

    // Очищаем предыдущую визуализацию
    scene.children.forEach(child => {
      if (child instanceof Line || (child instanceof Mesh && child.geometry instanceof SphereGeometry)) {
        scene.remove(child);
      }
    });

    // Если DEBUG выключен, не создаем визуализацию
    if (!DEBUG) return;

    // Создаем геометрию для кривой позиции
    const positionCurveGeometry = new BufferGeometry().setFromPoints(curveRef.current.getPoints(50));
    const positionCurveMaterial = new LineBasicMaterial({ color: 0xff0000 });
    const positionCurveObject = new Line(positionCurveGeometry, positionCurveMaterial);
    scene.add(positionCurveObject);

    // Создаем геометрию для кривой направления
    const lookAtCurveGeometry = new BufferGeometry().setFromPoints(lookAtCurveRef.current.getPoints(50));
    const lookAtCurveMaterial = new LineBasicMaterial({ color: 0x0000ff });
    const lookAtCurveObject = new Line(lookAtCurveGeometry, lookAtCurveMaterial);
    scene.add(lookAtCurveObject);

    // Создаем сферы для контрольных точек позиции
    const createControlPoint = (position, color) => {
      const geometry = new SphereGeometry(0.2, 16, 16);
      const material = new MeshBasicMaterial({ color });
      const sphere = new Mesh(geometry, material);
      sphere.position.set(...position);
      scene.add(sphere);
    };

    // Добавляем контрольные точки позиции
    CAMERA_ANIMATION.CURVE_POINTS.forEach((point, index) => {
      let color;
      if (index === 0) color = 0x00ff00;      // Зеленая - начальная точка
      else if (index === CAMERA_ANIMATION.CURVE_POINTS.length - 1) color = 0xff0000; // Красная - конечная точка
      else color = 0xffff00;                  // Желтые - контрольные точки
      
      createControlPoint(point, color);
    });

    // Добавляем контрольные точки направления
    CAMERA_ANIMATION.LOOK_AT_POINTS.forEach((point) => {
      createControlPoint(point, 0x0000ff); // Синие - точки направления
    });
  }, [scene]);

  // Handle scroll-based camera animation
  useEffect(() => {
    let targetScroll = 0;
    let currentScroll = 0;
    let animationFrameId = null;

    const checkEndPosition = (scroll) => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scroll / maxScroll;
      onEndPositionChange(progress >= 0.9);
    };

    const handleScroll = () => {
      targetScroll = window.scrollY;
      checkEndPosition(window.scrollY);
    };

    const animate = () => {
      if (!curveRef.current || !lookAtCurveRef.current) return;
      
      currentScroll += (targetScroll - currentScroll) * 0.1;
      checkEndPosition(currentScroll);
      
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = Math.min(currentScroll / maxScroll, 1);

      // Вычисляем текущий сегмент и прогресс внутри сегмента
      const totalSegments = CAMERA_ANIMATION.CURVE_POINTS.length - 1;
      const segmentProgress = scrollProgress * totalSegments;
      const currentSegment = Math.floor(segmentProgress);
      const segmentLocalProgress = segmentProgress - currentSegment;

      // Получаем точки для текущего сегмента
      const startPosition = new Vector3(...CAMERA_ANIMATION.CURVE_POINTS[currentSegment]);
      const endPosition = new Vector3(...CAMERA_ANIMATION.CURVE_POINTS[Math.min(currentSegment + 1, totalSegments)]);
      
      const startLookAt = new Vector3(...CAMERA_ANIMATION.LOOK_AT_POINTS[currentSegment]);
      const endLookAt = new Vector3(...CAMERA_ANIMATION.LOOK_AT_POINTS[Math.min(currentSegment + 1, totalSegments)]);

      // Интерполируем позицию и направление
      const currentPosition = new Vector3().lerpVectors(startPosition, endPosition, segmentLocalProgress);
      const currentLookAt = new Vector3().lerpVectors(startLookAt, endLookAt, segmentLocalProgress);
      
      // Плавно перемещаем камеру
      animationRef.current = gsap.to(camera.position, {
        x: currentPosition.x,
        y: currentPosition.y,
        z: currentPosition.z,
        duration: CAMERA_ANIMATION.ANIMATION.DURATION,
        ease: "power2.inOut",
        onUpdate: () => {
          camera.lookAt(currentLookAt.x, currentLookAt.y, currentLookAt.z);
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener('scroll', handleScroll);
    animate();

    // Initial check
    checkEndPosition(window.scrollY);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      if (animationRef.current) {
        animationRef.current.kill();
      }
    };
  }, [camera, onEndPositionChange]);

  // Следим за изменением состояния двигателя
  useEffect(() => {
    // Функция для анимации камеры к выхлопной трубе
    const animateToExhaust = () => {
      if (!isEngineOn) return;

      // Находим элемент с id "exhaust-anchor"
      const exhaustAnchor = document.getElementById('exhaust-anchor');
      if (!exhaustAnchor) return;

      // Плавно прокручиваем к якорю
      gsap.to(window, {
        scrollTo: {
          y: exhaustAnchor.offsetTop,
          autoKill: false
        },
        duration: 2,
        ease: "power2.inOut"
      });
    };

    if (isEngineOn) {
      animateToExhaust();
    }
  }, [isEngineOn]);

  return (
    <>
      <color args={[0,0,0]} attach="background" />
      
      <ambientLight intensity={0.1} />
      
      {isCarVisible && (
        <Suspense fallback={null}>
          <Car 
            isMoving={isMoving} 
            fadeSpeed={fadeSpeed} 
            isEngineOn={isEngineOn}
            soundGraphics={soundGraphics}
          />
          <group ref={smokeRef}>
            <ExhaustSmoke 
              isEngineOn={isEngineOn} 
              groupRef={smokeRef}
              soundGraphics={soundGraphics}
            />
          </group>
        </Suspense>
      )}
      {/* <Text3DComponent
        text="v1.6.0"
        height={0.05}
        scale={0.3}
        rotation={[-1.57,0,0]}
        position={[-4.94, 1, -2.238487502741235]}
        color={spotlightColors.primary}
      /> */}

      {buildings.map(building => (
        <Building 
          key={building.id}
          position={building.position}
          isMoving={isMoving}
        />
      ))}

      <spotLight 
        color={spotlightColors.primary}
        intensity={spotlightIntensity} 
        angle={0.9}
        penumbra={0.5}
        position={[5, 4, 0]} 
        castShadow
        shadow-bias={-0.0001}
        shadow-mapSize={[512, 512]}
        shadow-camera-far={20}
        shadow-camera-near={1}
        shadow-camera-left={-5}
        shadow-camera-right={5}
        shadow-camera-top={5}
        shadow-camera-bottom={-5}
      />
      <spotLight 
        color={spotlightColors.secondary}
        intensity={spotlightIntensitySecondary} 
        angle={0.9}
        penumbra={0.6}
        position={[-5, 4, 0]} 
        castShadow
        shadow-bias={-0.0001}
        shadow-mapSize={[512, 512]}
        shadow-camera-far={20}
        shadow-camera-near={1}
        shadow-camera-left={-5}
        shadow-camera-right={5}
        shadow-camera-top={5}
        shadow-camera-bottom={-5}
      />

      {groundType === 'asphalt' ? <Asphalt isMoving={isMoving} /> : <Ground isMoving={isMoving} />}
    </>
  );
}

export default function Experience() {
  const [isLoading, setIsLoading] = useState(true);
  const [soundGraphics, setSoundGraphics] = useState(null);
  const [isSoundEnabled, setIsSoundEnabled] = useState(false);
  const [colorScheme, setColorScheme] = useState(INTRO_COLORS.FIRST);

  const [isCarVisible, setIsCarVisible] = useState(false);
  const [isEngineOn, setIsEngineOn] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  const [groundType, setGroundType] = useState('asphalt');
  const [spotlightIntensity, setSpotlightIntensity] = useState(520);
  const [fadeSpeed] = useState(0.4);
  const [spotlightColors, setSpotlightColors] = useState({
    primary: [1, 0.25, 0.7],
    secondary: [0.14, 0.5, 1]
  });

  const [isAtEndPosition, setIsAtEndPosition] = useState(false);

  const handleColorSchemeChange = (newScheme) => {
    setColorScheme(newScheme);
  };

  return (
    <WebSocketProvider isCarVisible={isCarVisible}>
      <div style={{ height: '400vh', position: 'relative' }}>
        {/* Якоря для позиций камеры */}
        <div id="start-anchor" style={{ position: 'absolute', top: '0', height: '1px' }} />
        <div id="circle-anchor" style={{ position: 'absolute', top: '100vh', height: '1px' }} />
        <div id="end-anchor" style={{ position: 'absolute', top: '300vh', height: '1px' }} />

        <LoadingScreen 
          onLoadingComplete={() => setIsLoading(false)} 
          isSoundEnabled={isSoundEnabled}
          setIsSoundEnabled={setIsSoundEnabled}
          signatureColor={colorScheme.TEXT}
          onColorSchemeChange={handleColorSchemeChange}
        />
        <Canvas 
          shadows="basic"
          dpr={1}
          camera={{
            fov: 45,
            near: 0.1,
            far: 200,
            position: CAMERA_ANIMATION.INITIAL.POSITION
          }}
          performance={{ min: 0.5 }}
          gl={{ 
            antialias: false,
            powerPreference: "high-performance",
            alpha: false,
            stencil: false,
            depth: true,
            precision: "lowp"
          }}
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%',
            zIndex: 1
          }}
          onCreated={({ gl }) => {
            gl.setClearColor('#000000', 1);
          }}
        >
          <Scene 
            groundType={groundType} 
            spotlightColors={spotlightColors}
            isMoving={isMoving}
            isCarVisible={isCarVisible}
            spotlightIntensity={spotlightIntensity}
            fadeSpeed={fadeSpeed}
            isEngineOn={isEngineOn}
            soundGraphics={soundGraphics}
            onEndPositionChange={setIsAtEndPosition}
          />
          <Sound isEngineOn={isEngineOn} onGraphicsCreated={setSoundGraphics} />
        </Canvas>
        
        {!isLoading && (
          <>
            <Overlay 
              groundType={groundType} 
              setGroundType={setGroundType}
              spotlightColors={spotlightColors}
              setSpotlightColors={setSpotlightColors}
              isMoving={isMoving}
              setIsMoving={setIsMoving}
              isCarVisible={isCarVisible}
              setIsCarVisible={setIsCarVisible}
              spotlightIntensity={spotlightIntensity}
              setSpotlightIntensity={setSpotlightIntensity}
              isEngineOn={isEngineOn}
              setIsEngineOn={setIsEngineOn}
              soundGraphics={soundGraphics}
              signatureColor={colorScheme.TEXT}
              setSignatureColor={handleColorSchemeChange}
            />
            <div style={{
              position: 'fixed',
              top: '73%',
              right: 18,
              transform: 'translateY(-50%)',
              width: '300px',
              height: '200px',
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '10px',
              overflow: 'hidden',
              zIndex: 2
            }}>
              <SoundVisualization soundGraphics={soundGraphics} />
            </div>
            <EndText isAtEndPosition={isAtEndPosition} />
          </>
        )}
      </div>
    </WebSocketProvider>
  );
} 